import { defineStore } from "pinia";
import { computed, readonly, ref } from "vue";

import type { ToolActivityStep } from "@/composables/useChatStream";

import apiClient from "@/api/axios";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessage {
  /** Tool-call work log captured while this answer was generated (assistant
   *  messages only). Persists the "..  queried X → N" trail after streaming. */
  activity?: ToolActivityStep[];
  content: string;
  id: string;
  role: "assistant" | "user";
  timestamp: Date;
}

export interface ConversationSummary {
  _count: { messages: number };
  createdAt: string;
  createdBy: string;
  entityId: string;
  entityType: string;
  id: string;
  title: null | string;
  updatedAt: string;
}

export interface EntityContext {
  campaignId?: string;
  campaignName?: string;
  caseName?: string;
  entityId: string;
  entityType: string; // matches backend: 'case' | 'campaign' | 'firm'
  type: "campaign" | "case" | "firm";
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let messageCounter = 0;

function generateId(): string {
  return `msg_${Date.now()}_${++messageCounter}`;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useChatStore = defineStore("chat", () => {
  // ------ State ------
  const messages = ref<ChatMessage[]>([]);
  const conversationId = ref<null | string>(null);
  const entityContext = ref<EntityContext | null>(null);
  const isLoading = ref(false);
  const isStreaming = ref(false);
  const error = ref<null | string>(null);
  const conversations = ref<ConversationSummary[]>([]);
  const conversationsLoading = ref(false);

  // Bumped by $reset; a response from an older generation belongs to the previous user. A ref so
  // HandleAssistant can watch it and drop its own in-flight stream too.
  const generation = ref(0);

  // ------ Getters ------
  const chatHistory = computed(() => messages.value);
  const hasContext = computed(() => entityContext.value !== null);

  // ------ Actions ------

  /**
   * Initialize/reset the chat for a new context.
   */
  function initialize(welcomeMessage?: string) {
    messages.value = [];
    error.value = null;
    isLoading.value = false;
    isStreaming.value = false;

    if (welcomeMessage) {
      messages.value.push({
        content: welcomeMessage,
        id: generateId(),
        role: "assistant",
        timestamp: new Date(),
      });
    }
  }

  /**
   * Set the entity context (case, campaign, or firm).
   * This determines which entity the AI chat is about.
   */
  function setEntityContext(context: EntityContext) {
    entityContext.value = context;
  }

  /**
   * Clear all context and start completely fresh.
   */
  function clearContext() {
    entityContext.value = null;
    conversationId.value = null;
    messages.value = [];
    error.value = null;
    isLoading.value = false;
    isStreaming.value = false;
    conversations.value = [];
  }

  /**
   * Start a new conversation within the same entity context.
   * Keeps the entity context but resets conversation state.
   */
  function startNewConversation() {
    conversationId.value = null;
    messages.value = [];
    error.value = null;
    isLoading.value = false;
    isStreaming.value = false;
  }

  /**
   * Add a user message to the local message list.
   */
  function addUserMessage(content: string): ChatMessage {
    const msg: ChatMessage = {
      content,
      id: generateId(),
      role: "user",
      timestamp: new Date(),
    };
    messages.value.push(msg);
    return msg;
  }

  /**
   * Add an assistant message placeholder and return its ID so the caller
   * can update it as streaming text arrives.
   */
  function addAssistantPlaceholder(activity?: ToolActivityStep[]): string {
    const msg: ChatMessage = {
      content: "",
      id: generateId(),
      role: "assistant",
      timestamp: new Date(),
      ...(activity && activity.length > 0 ? { activity } : {}),
    };
    messages.value.push(msg);
    return msg.id;
  }

  /**
   * Update an assistant message's content (used during streaming).
   */
  function updateAssistantMessage(messageId: string, content: string) {
    const idx = messages.value.findIndex((m) => m.id === messageId);
    if (idx >= 0) {
      messages.value[idx] = { ...messages.value[idx], content };
    }
  }

  /**
   * Set the conversation ID (returned from the backend after first message).
   */
  function setConversationId(id: null | string) {
    conversationId.value = id;
  }

  // ------ Conversation history ------

  /**
   * Load the list of past conversations for the current entity context.
   */
  async function loadConversations() {
    if (!entityContext.value) return;

    conversationsLoading.value = true;
    const requestGeneration = generation.value;
    try {
      const response = await apiClient.get("/ai/conversations", {
        params: {
          entityId: entityContext.value.entityId,
          entityType: entityContext.value.entityType,
        },
      });
      if (requestGeneration !== generation.value) return;
      conversations.value = response.data.conversations || [];
    } catch {
      if (requestGeneration !== generation.value) return;
      conversations.value = [];
    } finally {
      if (requestGeneration === generation.value) conversationsLoading.value = false;
    }
  }

  /**
   * Load messages for a specific conversation (to restore a past conversation).
   */
  async function loadConversationMessages(convId: string) {
    isLoading.value = true;
    error.value = null;
    const requestGeneration = generation.value;
    try {
      const response = await apiClient.get(`/ai/conversations/${convId}/messages`, {
        params: { limit: 200, page: 1 },
      });
      if (requestGeneration !== generation.value) return;
      const apiMessages = response.data.messages || [];
      conversationId.value = convId;
      messages.value = apiMessages.map(
        (m: { content: string; createdAt: string; id: string; role: string }) => ({
          content: m.content,
          id: m.id,
          role: m.role as "assistant" | "user",
          timestamp: new Date(m.createdAt),
        })
      );
    } catch {
      if (requestGeneration !== generation.value) return;
      error.value = "Failed to load conversation messages.";
    } finally {
      if (requestGeneration === generation.value) isLoading.value = false;
    }
  }

  // ------ Reset ------

  /**
   * Drop every cached record. Pinia only gives options stores a working `$reset`; on a setup store
   * it's a no-op in production, so auth's clearAllStores (sign-out, or a magic-link account switch
   * with no page reload) used to leave the previous user's data here. Bumping `generation` stops a
   * request that was in flight at the reset from writing its response afterwards.
   */
  function $reset() {
    generation.value++;
    clearContext();
    conversationsLoading.value = false;
  }

  // ------ Expose ------

  return {
    $reset,
    conversationId,
    conversations,
    conversationsLoading,
    entityContext,
    error,
    generation: readonly(generation),
    isLoading,
    isStreaming,
    // State
    messages,

    // Getters
    chatHistory,
    hasContext,

    addAssistantPlaceholder,
    addUserMessage,
    clearContext,
    // Actions
    initialize,
    loadConversationMessages,
    loadConversations,
    setConversationId,
    setEntityContext,
    startNewConversation,
    updateAssistantMessage,
  };
});
